import { useMemo } from 'react';
import { cn } from '@rocco/utils/cn';
import { TextTypeEnum } from 'generated/graphql';
import type {
    PageBuilderModuleMetaProps,
    TextColumnProps,
} from '@modules/page-builder/types';
import RichTextDisplay from '../../common/rich-text/display/RichTextDisplay';
import { useWebsiteTrackingProxy } from '~/modules/tracking/hook/useWebsiteTrackingProxy';

interface SingleParagraphModuleProps extends PageBuilderModuleMetaProps {
    column: TextColumnProps;
    /**
     * The text type (paragraph, h3, quote). If not provided, gets it from the
     * column properties.
     */
    textType?: TextTypeEnum;
}

/**
 * A rendered Page Builder module for a single paragraph. Can be a paragraph,
 * heading, or quote.
 *
 * The editor version is at
 * {@link packages/web/app/modules/page-builder/component/edit/single-paragraph-module/SingleParagraphModule.tsx}
 */
const SingleParagraphModule = ({
    column,
    meta,
    textType,
}: SingleParagraphModuleProps) => {
    const derivedTextType = useMemo(
        () => textType ?? column.textType ?? TextTypeEnum.Paragraph,
        [textType, column.textType],
    );

    const clickProxy = useWebsiteTrackingProxy({
        professionalID: meta?.professional?.id,
        professionalTitle: meta?.professional?.title,
        itemType: meta?.itemType,
    });

    return (
        <div
            className={cn(
                'mx-auto w-full px-4.5 md:max-w-225',
                derivedTextType === TextTypeEnum.H3 && 'md:max-w-310',
            )}
            onClick={clickProxy}
            onContextMenu={clickProxy}
        >
            <RichTextDisplay contentHTML={column.contentHTML} />
        </div>
    );
};

export default SingleParagraphModule;
